import {
  ListItem,
  Typography,
  Box,
  useTheme,
  Tooltip,
} from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { TasksResponse } from "@/modules/tasks/dtos";
import {
  checkIfTaskIsSubmittedOverdue,
  formatSmartDate,
  capitalizeFirstLetter,
} from "@/modules/tasks/utils";
import { TaskStatusesBackend } from "@/modules/tasks/enums";
import CommonAvatar from "@/modules/common/components/ui-kit/common-avatar";
import TaskCompletionCheckbox from "@/modules/tasks/components/task-completion-checkbox";

interface TaskItemProps {
  task: TasksResponse;
  onCheckboxChange?: (task: TasksResponse) => void;
  isLoading?: boolean;
  onClick?: () => void;
}

const TaskItem = ({
  task,
  onCheckboxChange,
  isLoading = false,
  onClick,
}: TaskItemProps) => {
  const theme = useTheme();
  const isCompleted = task?.status === TaskStatusesBackend.completed;

  const handleToggleComplete = () => {
    onCheckboxChange?.(task);
  };

  return (
    <ListItem
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.xs,
        padding: "12px 16px",
        borderBottom: `1px solid ${nawyTheme.nawy.colors.semantic.border.secondary}`,
        width: "100%",
      }}
    >
      {/* Row 1: Icon Button + Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.xs,
          width: "100%",
        }}
      >
        <TaskCompletionCheckbox
          checked={isCompleted}
          onChange={handleToggleComplete}
          disabled={false}
          size={20}
          isLoading={isLoading}
        />

        {/* Title */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.xs,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Tooltip
            title={capitalizeFirstLetter(task?.title)}
            placement="top"
            arrow
          >
            <Typography
              sx={{
                color: isCompleted
                  ? theme.nawy.colors.semantic.text.tertiary
                  : theme.nawy.colors.semantic.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                fontFamily: nawyTheme.nawy.typography.english.fontFamily,
              }}
            >
              {capitalizeFirstLetter(task?.title)}
            </Typography>
          </Tooltip>

          {/* Row 2: Due Date */}
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
              color: checkIfTaskIsSubmittedOverdue(
                task?.status,
                task?.dueDate,
                task?.completedAt || ""
              )
                ? nawyTheme.nawy.colors.semantic.text.extension.negative
                : theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            {formatSmartDate(task.dueDate)}
          </Typography>

          {/* Row 3: User Avatar */}
          <Box
            sx={{
              display: "flex",
              gap: theme.nawy.spacing.xs,
              alignItems: "center",
              width: "100%",
            }}
          >
            <CommonAvatar
              name={task.User?.name || "System"}
              color={
                task.User?.color || theme.nawy.colors.semantic.text.tertiary
              }
              size={24}
              fontSize={theme.nawy.typography.english.variants.body.md.regular}
            />

            <Typography
              sx={{
                ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {task.User?.name}
            </Typography>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

export default TaskItem;
